import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Logo } from '@/components/Logo/Logo';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/auth/AuthProvider';
import { ProjectSwitcher } from './ProjectSwitcher';
import { RecentRecords } from './RecentRecords';
import { apiFetch } from '@/api/client';
import { NAV_GROUPS, navItemActive, type NavItem } from './navConfig';
import type { FeatureFlagKey } from '@marketing-hub/shared';
import { useFeatureFlags } from '@/features/platform/useFeatureFlags';
import styles from './Sidebar.module.css';

const ROLE_RANK: Record<string, number> = {
  read_only: 0,
  sales: 1,
  marketing: 2,
  admin: 3,
  owner: 4,
};

function canSee(
  item: NavItem,
  rank: number,
  isEnabled: (key: FeatureFlagKey) => boolean,
): boolean {
  if (item.featureFlag && !isEnabled(item.featureFlag)) return false;
  if (!item.minRole) return true;
  const need = ROLE_RANK[item.minRole] ?? 0;
  return rank >= need;
}

export function Sidebar() {
  const { logout } = useAuth();
  const { pathname } = useLocation();
  const orgQ = useQuery({
    queryKey: ['org'],
    queryFn: () => apiFetch<{ organization: { my_role: string } }>('/api/org'),
  });
  const rank = ROLE_RANK[orgQ.data?.organization.my_role ?? 'owner'] ?? 0;
  const { isEnabled } = useFeatureFlags();

  return (
    <>
      <Logo />
      <ProjectSwitcher />
      {NAV_GROUPS.map((group) => {
        const items = group.items.filter((i) => canSee(i, rank, isEnabled));
        if (!items.length) return null;
        return (
          <nav key={group.label} aria-label={group.label}>
            <div className={styles.sectionTitle}>{group.label}</div>
            <ul className={styles.navList}>
              {items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      [
                        styles.navLink,
                        navItemActive(pathname, item, isActive) ? styles.navLinkActive : '',
                      ]
                        .filter(Boolean)
                        .join(' ')
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        );
      })}
      <RecentRecords />
      <div className={styles.footer}>
        <Button variant="ghost" className={styles.logout} onClick={() => void logout()}>
          Uitloggen
        </Button>
      </div>
    </>
  );
}
